/**
 * RadialMenu — the signature interaction (spec §9). Right-click a node → the
 * operators VALID for that node type appear on a RING around the cursor
 * (registry-driven; invalid verbs don't appear). Hover a spoke to read it in the hub.
 */
import { useState } from 'react';
import { useFusionStore } from '../../stores/useFusionStore';
import { operatorsForType, STATUS_META, type Operator } from '../../case-canvas/registry';
import { runOperator } from '../../case-canvas/jobs';
import { useCaseWriteBlock } from '../../case-canvas/caseAccess';

const R = 84;          // ring radius
const HUB = 60;        // centre hub diameter

export function RadialMenu() {
  const radial = useFusionStore((s) => s.radial);
  const close = useFusionStore((s) => s.closeRadial);
  const [hover, setHover] = useState<Operator | null>(null);
  const { blocked, reason: blockedReason } = useCaseWriteBlock();
  if (!radial) return null;

  const ops = operatorsForType(radial.nodeType);
  const cx = Math.max(R + 24, Math.min(radial.x, window.innerWidth - R - 24));
  const cy = Math.max(R + 24, Math.min(radial.y, window.innerHeight - R - 24));
  const n = Math.max(ops.length, 1);
  const spoke = (i: number) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
  };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={close} onContextMenu={(e) => { e.preventDefault(); close(); }} />
      {/* connectors */}
      <svg className="fixed inset-0 z-40 pointer-events-none" width={window.innerWidth} height={window.innerHeight}>
        {ops.map((_, i) => { const s = spoke(i); return <line key={i} x1={cx} y1={cy} x2={s.x} y2={s.y} stroke="#94a3b8" strokeWidth={1.5} strokeOpacity={0.4} />; })}
      </svg>

      {/* centre hub */}
      <div className="fixed z-50 rounded-full border-2 border-sky-500 bg-card shadow-xl grid place-items-center text-center px-1"
        onContextMenu={(e) => e.preventDefault()}
        style={{ left: cx, top: cy, width: HUB, height: HUB, transform: 'translate(-50%,-50%)' }}>
        {hover ? (
          <div className="leading-tight">
            <div className="text-[9px] font-semibold truncate max-w-[52px]">{hover.name.replace(/^.*▸ /, '')}</div>
            <div className="text-[8px] uppercase" style={{ color: STATUS_META[hover.status].color }}>{STATUS_META[hover.status].label}</div>
          </div>
        ) : (
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{radial.nodeType || 'node'}</div>
        )}
      </div>

      {/* spokes */}
      {ops.length === 0 && (
        <div className="fixed z-50 text-[10px] text-muted-foreground bg-card border border-border rounded px-2 py-1 shadow"
          style={{ left: cx, top: cy + HUB / 2 + 10, transform: 'translate(-50%,0)' }}>
          no operators for this type
        </div>
      )}
      {ops.map((op, i) => {
        const s = spoke(i);
        const m = STATUS_META[op.status];
        return (
          <button key={op.id}
            title={blocked ? `${op.name} — ${blockedReason}` : m.live ? `${op.name} — ${m.label} · ${op.binding}` : `${op.name} — ${m.label} · unavailable — ${op.unavailable || op.binding}`}
            disabled={!m.live || blocked}
            data-testid={`radial-operator-${op.id}`}
            onClick={() => runOperator(op, { nodeId: radial.nodeId, nodeType: radial.nodeType })}
            onContextMenu={(e) => e.preventDefault()}
            onMouseEnter={() => setHover(op)} onMouseLeave={() => setHover(null)}
            className="fixed z-50 w-10 h-10 rounded-full border border-border bg-card shadow-lg grid place-items-center hover:border-primary transition-colors disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:border-border"
            style={{ left: s.x, top: s.y, transform: 'translate(-50%,-50%)' }}>
            <op.icon size={16} className="text-foreground" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full border border-card" style={{ background: m.color }} />
          </button>
        );
      })}
    </>
  );
}
